import { procedureRunPath } from '@/lib/procedureRunPath'
import type { ProcedureType } from '@/types/cgt'

/**
 * The run a form opened on its own page (/forms/:id) belongs to, and where that
 * page links back to. Kept out of FormRendererPage so it is unit-tested.
 *
 * `patientMrn` is not always a patient's: a kit run writes its supply id there,
 * and an MES order is a work order's. Reading it as an MRN on those runs
 * fetched `/patients/KIT-26-0001` (a 404), drew a patient banner for a person
 * who does not exist, and linked the crumbs and the return into Patients.
 */
export interface FormRun {
  /** The run's template type; null for a form outside a procedure, or while it is unknown. */
  type: ProcedureType | null | undefined
  procedureId: string | null | undefined
  patientMrn: string | null | undefined
  /** An MES order's minted number, its name in the MES trail. */
  mfgNumber?: string | null
}

/** Whether the run belongs to a patient: anything but a kit run or an MES order. */
export function hasPatient(run: FormRun): boolean {
  return !!run.patientMrn && run.type !== 'KitCreation' && run.type !== 'MES'
}

/** What the form's header calls `patientMrn`: a kit's supply id is not a patient's MRN. */
export function recordLabel(run: FormRun): string {
  return run.type === 'KitCreation' ? 'Kit' : 'Patient MRN'
}

/** The trail to the form, through the run's own list and page. */
export function formCrumbs(run: FormRun, formLabel: string): { label: string; to?: string }[] {
  const id = run.procedureId
  if (run.type === 'KitCreation') {
    return [
      { label: 'Kits', to: '/kits' },
      ...(id ? [{ label: run.patientMrn || 'Kit run', to: procedureRunPath(run.type, id, null)! }] : []),
      { label: formLabel },
    ]
  }
  if (run.type === 'MES') {
    return [
      { label: 'MES', to: '/mes' },
      ...(id ? [{ label: run.mfgNumber || 'MES order', to: procedureRunPath(run.type, id, null)! }] : []),
      { label: formLabel },
    ]
  }
  const mrn = run.patientMrn
  const patientPath = mrn ? `/patients/${encodeURIComponent(mrn)}` : null
  return [
    { label: 'Patients', to: '/patients' },
    ...(mrn && patientPath
      ? [
          { label: mrn, to: patientPath },
          ...(id ? [{ label: 'Procedure', to: `${patientPath}/procedures/${id}` }] : []),
        ]
      : []),
    { label: formLabel },
  ]
}

/**
 * Where completing the form returns to: the run's page, or the patient when the
 * form is not on a run. Null when there is nowhere to go, and the page stays put.
 */
export function formReturn(run: FormRun): { path: string; label: string } | null {
  if (run.procedureId) {
    const path = procedureRunPath(run.type ?? null, run.procedureId, run.patientMrn ?? null)
    if (!path) return null
    if (run.type === 'KitCreation') return { path, label: 'the kit run' }
    if (run.type === 'MES') return { path, label: 'the MES order' }
    return { path, label: 'the patient journey' }
  }
  if (hasPatient(run)) return { path: `/patients/${encodeURIComponent(run.patientMrn!)}`, label: 'the patient journey' }
  return null
}
